import { getElementAtPosition } from '../../lib/collision';
import { getSelectionAbsoluteCoords } from '../../lib/coords';
import { defaultGenericElement } from '../../lib/element';
import { getPlugin } from '../../lib/plugin';
import { renderInteractiveScene } from '../../lib/scene';
import {
  isHittingAnySelectedElementHandle,
  getSelectedElements,
  mergeSelectionIds,
} from '../../lib/selection';
import { dragNewElement } from '../../lib/transformation';
import {
  updateRuntimeStore,
  useEditorRuntimeStore,
} from '../../stores/useEditorRuntimeStore';
import { updateEditorStore, useEditorStore } from '../../stores/useEditorStore';

import { type GenericPolyElement } from '.';

export const togglePolyPointSelection = (
  pointId: string,
  selected: string[],
): string[] => {
  return selected.includes(pointId)
    ? selected.filter((id) => id !== pointId)
    : [...selected, pointId];
};

export const getPolyPointIdsInRect = (
  element: GenericPolyElement,
  selectionRect: Parameters<typeof getSelectionAbsoluteCoords>[0],
): string[] => {
  const [x1, y1, x2, y2] = getSelectionAbsoluteCoords(selectionRect);
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);

  return element.points
    .map((point, index) => ({
      absX: element.x + point[0],
      absY: element.y + point[1],
      index,
    }))
    .filter(
      ({ absX, absY }) =>
        absX >= minX && absX <= maxX && absY >= minY && absY <= maxY,
    )
    .map(({ index }) => index.toString());
};

export const handlePolyPointPointerDown = (
  pos: { x: number; y: number },
  original: PointerEvent,
) => {
  const runtimeState = useEditorRuntimeStore.getState();

  if (runtimeState.activeToolSubMode === 'poly_delete_point') return;

  const elementState = useEditorStore.getState();
  const selectedElements = getSelectedElements();
  const element = selectedElements[0] as GenericPolyElement | undefined;

  if (!element || element.isLocked) return;

  const plugin = getPlugin(element.type);
  if (!plugin.selection?.hasVisiblePointHandles) return;

  const resize = isHittingAnySelectedElementHandle(
    [pos.x, pos.y],
    selectedElements,
  );

  const pointId = resize?.split('-')[0] ?? '';
  const isMainPoint =
    resize != null &&
    !resize.includes('handle') &&
    !Number.isNaN(Number.parseInt(pointId, 10));

  if (isMainPoint) {
    const isMultiSelect =
      original.ctrlKey || original.metaKey || original.shiftKey;
    const current = elementState.selectedSubElementIds;
    const alreadySelected = current.includes(pointId);

    if (isMultiSelect) {
      const next = togglePolyPointSelection(pointId, current);
      updateEditorStore({ selectedSubElementIds: next });

      if (next.includes(pointId)) {
        startPolyPointMove(pos, pointId);
      }
    } else if (alreadySelected && current.length > 1) {
      startPolyPointMove(pos, pointId);
    } else {
      updateEditorStore({ selectedSubElementIds: [pointId] });
      startPolyPointMove(pos, pointId);
    }
    return;
  }

  if (resize != null) {
    updateEditorStore({ selectedSubElementIds: [pointId] });
    startPolyPointMove(pos, resize);
    return;
  }

  const isAdditive = original.ctrlKey || original.metaKey || original.shiftKey;
  if (!isAdditive) {
    updateEditorStore({ selectedSubElementIds: [] });
  }

  updateRuntimeStore({
    selectionElement: {
      ...defaultGenericElement(),
      originX: pos.x,
      originY: pos.y,
      type: 'selection',
      x: pos.x,
      y: pos.y,
    },
  });
};

const startPolyPointMove = (pos: { x: number; y: number }, type: string) => {
  const elementState = useEditorStore.getState();
  updateRuntimeStore({
    activeTransformation: {
      hasChanges: false,
      origin: [pos.x, pos.y],
      originalElements: new Map(elementState.elements),
      type,
    },
  });
};

export const handlePolyPointRectSelectMove = (pos: {
  x: number;
  y: number;
}) => {
  const runtimeState = useEditorRuntimeStore.getState();
  const elementState = useEditorStore.getState();

  if (!runtimeState.selectionElement) return;

  const element = elementState.elements.get(
    elementState.selectedElementIds[0],
  ) as GenericPolyElement | undefined;

  if (!element) return;

  const updatedRect = {
    ...runtimeState.selectionElement,
    ...dragNewElement({
      ...runtimeState.selectionElement,
      x: pos.x,
      y: pos.y,
    }),
  };

  updateRuntimeStore({ selectionElement: updatedRect });

  const idsInRect = getPolyPointIdsInRect(element, updatedRect);

  updateEditorStore({
    selectedSubElementIds: mergeSelectionIds(
      idsInRect,
      elementState.selectedSubElementIds,
    ),
  });

  renderInteractiveScene();
};

export const handlePolyPointPointerUp = (pos: { x: number; y: number }) => {
  const runtimeState = useEditorRuntimeStore.getState();
  const elementState = useEditorStore.getState();

  if (!runtimeState.selectionElement) return;

  const hasDragged =
    runtimeState.selectionElement.width > 1 ||
    runtimeState.selectionElement.height > 1;

  if (!hasDragged) {
    const hitElement = getElementAtPosition(pos.x, pos.y);
    const selectedId = elementState.selectedElementIds[0];
    const isOutsidePoly = !hitElement || hitElement.id !== selectedId;

    if (isOutsidePoly) {
      updateEditorStore({
        polygonPointsVisible: false,
        selectedSubElementIds: [],
      });
    }
  }

  updateRuntimeStore({ selectionElement: undefined });
  renderInteractiveScene();
};
